import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Live cameras via the go2rtc gateway, as a mini DVR wall. The backend hands us the
// browser-reachable go2rtc base + stream list; we embed each stream directly.
// Camera GROUPS + tile ORDER + tiles-per-row are saved per-browser (localStorage).
// Admin-only (Security hub). Shows a connect panel when GO2RTC_URL isn't set.

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

function SectionHeader({ title, subtitle, right }) {
  return (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

const streamUrl = (base, name) => `/camplayer.html?base=${encodeURIComponent(base)}&src=${encodeURIComponent(name)}`;

// ---- per-browser group storage ----
const loadGroups = () => { try { return JSON.parse(localStorage.getItem('camGroups') || '[]'); } catch { return []; } };
const saveGroupsLS = (g) => { try { localStorage.setItem('camGroups', JSON.stringify(g)); } catch { /* ignore */ } };
const uid = () => Math.random().toString(36).slice(2, 9);

export default function Cameras() {
  const [cfg, setCfg] = useState(null);
  const [full, setFull] = useState(null);
  const [cols, setCols] = useState(() => { const v = Number(localStorage.getItem('camCols')); return v >= 1 && v <= 4 ? v : 3; });
  const pickCols = (n) => { setCols(n); try { localStorage.setItem('camCols', String(n)); } catch { /* ignore */ } };
  const [groups, setGroups] = useState(loadGroups);
  const [sel, setSel] = useState(() => localStorage.getItem('camGroupSel') || 'all');
  const [managing, setManaging] = useState(false);

  const updateGroups = (g) => { setGroups(g); saveGroupsLS(g); };
  const pickSel = (id) => { setSel(id); try { localStorage.setItem('camGroupSel', id); } catch { /* ignore */ } };

  const load = useCallback(() => {
    axios.get(API + '/cameras', { headers: headers() }).then(r => setCfg(r.data)).catch(() => setCfg({ configured: false, streams: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div style={{ padding: 40, color: '#9CA3AF', fontSize: 14 }}>Loading cameras…</div>;

  if (!cfg.configured) {
    return (
      <div>
        <SectionHeader title="Cameras" subtitle="Live UniFi Protect views via the go2rtc gateway." />
        <div style={{ ...card, padding: '40px 32px', maxWidth: 640 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Camera gateway not connected</div>
          <p style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.6, margin: 0 }}>
            Set <code>GO2RTC_URL</code> on the server to your go2rtc gateway's browser-reachable
            address and add camera streams to its <code>go2rtc.yaml</code>. Then this tab lists them automatically.
          </p>
        </div>
      </div>
    );
  }

  const allStreams = cfg.streams || [];
  const byName = Object.fromEntries(allStreams.map(s => [s.name, s]));
  const group = groups.find(g => g.id === sel);
  const shown = (sel === 'all' || !group)
    ? allStreams
    : (group.cameras || []).map(n => byName[n]).filter(Boolean);

  const tabBtn = (active) => ({
    padding: '6px 13px', border: '1px solid ' + (active ? NAVY : '#E2E6EE'), borderRadius: 999,
    background: active ? NAVY : '#fff', color: active ? '#fff' : NAVY, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div>
      <SectionHeader
        title="Cameras"
        subtitle="Live views from UniFi Protect. Click a camera to expand."
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'inline-flex', background: '#F1F2F4', borderRadius: 10, padding: 3, gap: 2 }} title="Tiles per row">
              {[1, 2, 3, 4].map(n => {
                const on = cols === n;
                return (
                  <button key={n} onClick={() => pickCols(n)} title={`${n} per row`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '7px 9px', border: 'none', borderRadius: 8, cursor: 'pointer', background: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 2px rgba(0,0,0,0.14)' : 'none' }}>
                    {Array.from({ length: n }).map((_, i) => (
                      <span key={i} style={{ width: 4, height: 13, borderRadius: 1, background: on ? NAVY : '#9CA3AF' }} />
                    ))}
                  </button>
                );
              })}
            </div>
            <button onClick={load} style={{ padding: '8px 14px', borderRadius: 9, border: `1px solid ${NAVY}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Refresh</button>
          </div>
        }
      />

      {/* Group selector */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={() => pickSel('all')} style={tabBtn(sel === 'all')}>All ({allStreams.length})</button>
        {groups.map(g => {
          const n = (g.cameras || []).filter(c => byName[c]).length;
          return <button key={g.id} onClick={() => pickSel(g.id)} style={tabBtn(sel === g.id)}>{g.name} ({n})</button>;
        })}
        <button onClick={() => setManaging(true)} style={{ ...tabBtn(false), border: '1px dashed #C7CDD8', color: '#6B7280' }}>⚙ Manage groups</button>
      </div>

      {allStreams.length === 0 && (
        <div style={{ ...card, padding: 28, fontSize: 13.5, color: '#b45309' }}>
          No streams found. Is go2rtc running and reachable at <code>{cfg.base}</code>, with cameras in its <code>go2rtc.yaml</code>?
        </div>
      )}
      {allStreams.length > 0 && shown.length === 0 && (
        <div style={{ ...card, padding: 28, fontSize: 13.5, color: '#6B7280' }}>
          No cameras in this group yet — add some via <strong>⚙ Manage groups</strong>.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 16 }}>
        {shown.map(s => (
          <div key={s.name} style={{ ...card, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', borderBottom: '1px solid #EDEDF0' }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY }}>{s.label}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setFull(s)} title="Expand"
                  style={{ border: '1px solid #e2e6ee', background: '#fff', borderRadius: 7, padding: '4px 9px', fontSize: 12, fontWeight: 600, color: NAVY, cursor: 'pointer' }}>⤢ Expand</button>
                <a href={streamUrl(cfg.base, s.name)} target="_blank" rel="noreferrer" title="Open in new tab"
                  style={{ border: '1px solid #e2e6ee', background: '#fff', borderRadius: 7, padding: '4px 9px', fontSize: 12, fontWeight: 600, color: NAVY, textDecoration: 'none' }}>↗</a>
              </div>
            </div>
            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', background: '#0B1220' }}>
              <iframe title={s.label} src={streamUrl(cfg.base, s.name)}
                allow="autoplay; fullscreen" allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        ))}
      </div>

      {full && (
        <div onClick={() => setFull(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,20,0.92)', zIndex: 1000, display: 'flex', flexDirection: 'column', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{full.label}</div>
            <button onClick={() => setFull(null)} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 14, cursor: 'pointer' }}>✕ Close</button>
          </div>
          <div onClick={e => e.stopPropagation()} style={{ flex: 1, borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            <iframe title={full.label + ' (fullscreen)'} src={streamUrl(cfg.base, full.name)}
              allow="autoplay; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />
          </div>
        </div>
      )}

      {managing && (
        <ManageGroups groups={groups} streams={allStreams} onChange={updateGroups} onClose={() => setManaging(false)} />
      )}
    </div>
  );
}

// ---- Manage groups modal: create/rename/delete + assign & reorder cameras ----
function ManageGroups({ groups, streams, onChange, onClose }) {
  const [newName, setNewName] = useState('');
  const byName = Object.fromEntries(streams.map(s => [s.name, s]));

  const addGroup = () => { if (!newName.trim()) return; onChange([...groups, { id: uid(), name: newName.trim(), cameras: [] }]); setNewName(''); };
  const rename = (id, name) => onChange(groups.map(g => g.id === id ? { ...g, name } : g));
  const del = (id) => onChange(groups.filter(g => g.id !== id));
  const addCam = (id, name) => onChange(groups.map(g => g.id === id ? { ...g, cameras: [...(g.cameras || []), name] } : g));
  const removeCam = (id, name) => onChange(groups.map(g => g.id === id ? { ...g, cameras: (g.cameras || []).filter(n => n !== name) } : g));
  const move = (id, idx, dir) => onChange(groups.map(g => {
    if (g.id !== id) return g;
    const c = [...(g.cameras || [])]; const j = idx + dir;
    if (j < 0 || j >= c.length) return g;
    [c[idx], c[j]] = [c[j], c[idx]];
    return { ...g, cameras: c };
  }));

  const btn = { border: '1px solid #e2e6ee', background: '#fff', borderRadius: 7, padding: '3px 8px', fontSize: 12, fontWeight: 600, color: NAVY, cursor: 'pointer' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 'min(720px, 100%)', padding: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: NAVY, margin: 0 }}>Manage camera groups</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addGroup()}
            placeholder="New group name (e.g. Galleries)"
            style={{ flex: 1, padding: '9px 11px', border: '1px solid #dfe3ea', borderRadius: 8, fontSize: 13 }} />
          <button onClick={addGroup} style={{ padding: '9px 16px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add group</button>
        </div>

        {groups.length === 0 && <div style={{ fontSize: 13, color: '#9CA3AF' }}>No groups yet. Create one above, then add cameras to it.</div>}

        {groups.map(g => {
          const inGroup = (g.cameras || []);
          const available = streams.filter(s => !inGroup.includes(s.name));
          return (
            <div key={g.id} style={{ border: '1px solid #EDEDF0', borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <input value={g.name} onChange={e => rename(g.id, e.target.value)}
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #dfe3ea', borderRadius: 8, fontSize: 14, fontWeight: 600, color: NAVY }} />
                <button onClick={() => del(g.id)} style={{ ...btn, border: '1px solid #f2c4c4', color: '#b91c1c' }}>Delete</button>
              </div>

              {inGroup.length === 0 && <div style={{ fontSize: 12.5, color: '#9CA3AF', marginBottom: 8 }}>No cameras yet — add one below. Order here = tile order.</div>}
              {inGroup.map((name, idx) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: idx ? '1px solid #F3F4F6' : 'none' }}>
                  <span style={{ width: 20, color: '#9CA3AF', fontSize: 12, textAlign: 'right' }}>{idx + 1}.</span>
                  <span style={{ flex: 1, fontSize: 13, color: byName[name] ? NAVY : '#b91c1c', fontWeight: 600 }}>
                    {byName[name] ? byName[name].label : `${name} (missing)`}
                  </span>
                  <button onClick={() => move(g.id, idx, -1)} disabled={idx === 0} style={{ ...btn, opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                  <button onClick={() => move(g.id, idx, 1)} disabled={idx === inGroup.length - 1} style={{ ...btn, opacity: idx === inGroup.length - 1 ? 0.4 : 1 }}>↓</button>
                  <button onClick={() => removeCam(g.id, name)} style={{ ...btn, border: '1px solid #f2c4c4', color: '#b91c1c' }}>✕</button>
                </div>
              ))}

              {available.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <select value="" onChange={e => e.target.value && addCam(g.id, e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #dfe3ea', borderRadius: 8, fontSize: 13, color: NAVY }}>
                    <option value="">+ Add camera…</option>
                    {available.map(s => <option key={s.name} value={s.name}>{s.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 6 }}>Groups are saved in this browser only.</div>
      </div>
    </div>
  );
}
